import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, Input, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import XoePatientChip from './XoePatientChip';
import { confirmDeleteConversation } from '../../utils/xoe/confirmDeleteConversation';
import type { XoeConversationRailRowProps } from '../../types/xoe/XoeUiTypes';

dayjs.extend(relativeTime);

const { Text } = Typography;

const XoeConversationRailRow: React.FC<XoeConversationRailRowProps> = ({
	conversation,
	isActive,
	onSelect,
	onRename,
	onArchive,
	onDelete,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(conversation.title);
	const inputRef = useRef<any>(null);
	// Enter commits and then blurs, which would commit a second time. One latch per edit.
	const committedRef = useRef(false);

	useEffect(() => {
		if (editing) {
			setDraft(conversation.title);
			committedRef.current = false;
		}
	}, [editing, conversation.title]);

	// A bound conversation's title IS the patient's name (boundPatientName relies on it), so it cannot be renamed.
	const renameBlocked = conversation.patient_id != null;

	const commitRename = useCallback(() => {
		if (committedRef.current) return;
		committedRef.current = true;
		setEditing(false);
		const next = draft.trim();
		// Whitespace-only reverts with no request — an empty title is not an edit.
		if (!next || next === conversation.title) return;
		onRename(conversation.uuid, next);
	}, [draft, conversation.title, conversation.uuid, onRename]);

	const cancelRename = useCallback(() => {
		committedRef.current = true;
		setEditing(false);
	}, []);

	const menuItems: MenuProps['items'] = [
		{
			key: 'rename',
			label: renameBlocked ? (
				<Tooltip title="This conversation is named after the patient it's linked to.">
					<span data-testid="xoe-conversation-rename" data-disabled="true">
						Rename
					</span>
				</Tooltip>
			) : (
				<span data-testid="xoe-conversation-rename">Rename</span>
			),
			disabled: renameBlocked,
		},
		{
			key: 'archive',
			label: <span data-testid="xoe-conversation-archive">Archive</span>,
		},
		{
			key: 'delete',
			danger: true,
			label: <span data-testid="xoe-conversation-delete">Delete</span>,
		},
	];

	const handleMenuClick: NonNullable<MenuProps['onClick']> = ({ key, domEvent }) => {
		domEvent.stopPropagation();
		if (key === 'rename') {
			if (renameBlocked) return;
			setEditing(true);
			// The Dropdown is unmounting as this runs; focus after the commit.
			window.setTimeout(() => inputRef.current?.focus?.({ cursor: 'all' }), 0);
			return;
		}
		if (key === 'archive') onArchive(conversation.uuid);
		if (key === 'delete') confirmDeleteConversation(() => onDelete(conversation.uuid));
	};

	const stamp = conversation.last_message_at ?? conversation.created_at;

	return (
		<div
			className={`mg-xoe-row${isActive ? ' is-active' : ''}`}
			data-testid="xoe-conversation-row"
			data-active={String(isActive)}
			role="button"
			tabIndex={0}
			onClick={() => !editing && onSelect(conversation)}
			onKeyDown={(e) => {
				if (editing) return;
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect(conversation);
				}
			}}
		>
			<div className="mg-xoe-row-body">
				{editing ? (
					<Input
						ref={inputRef}
						size="small"
						value={draft}
						data-testid="xoe-conversation-rename-input"
						aria-label="Conversation title"
						onChange={(e) => setDraft(e.target.value)}
						onClick={(e) => e.stopPropagation()}
						onPressEnter={commitRename}
						onBlur={commitRename}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								e.preventDefault();
								cancelRename();
							}
						}}
					/>
				) : (
					<div className="mg-xoe-row-title">
						{conversation.title || 'Untitled conversation'}
					</div>
				)}

				<div className="mg-xoe-row-meta">
					{conversation.patient_id != null && (
						<XoePatientChip name={conversation.title || 'Patient linked'} />
					)}
					{stamp && <Text type="secondary" className="mg-xoe-row-stamp">{dayjs(stamp).fromNow()}</Text>}
				</div>
			</div>

			<Dropdown
				trigger={['click']}
				placement="bottomRight"
				menu={{ items: menuItems, onClick: handleMenuClick }}
			>
				<span
					className="mg-xoe-row-menu mg-xoe-focus-ring"
					data-testid="xoe-conversation-row-menu"
					role="button"
					tabIndex={0}
					aria-label="Conversation actions"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<MoreOutlined />
				</span>
			</Dropdown>
		</div>
	);
};

export default XoeConversationRailRow;
